/**
 * SalesAllocationMassUploadDialog — bulk insert MANY Sales Allocation rows in
 * one shot. Converted from Access `frmCtrlSalesQrySaleAlcAddMany` ("Mass Upload"
 * / `btnInsert` on the Sales Allocation tab, form `frmCtrlSalesMainSubFormSaleAlc`).
 *
 * Access shape (ground truth):
 *   - `btnInsert_Click` just opens `frmCtrlSalesQrySaleAlcAddMany` (no row
 *     precondition). `Form_Load` DELETEs the temp table `public_tmp_addmany_salesalc`.
 *   - The form is a subform datasheet bound to that temp table — a plain paste
 *     target, every cell FREE-TEXT (no ComboBox, no `strCheck`, no required field).
 *   - `btnSave_Click` scans every temp row (per-row ElseIf, `error` sticky → the
 *     FIRST failing row aborts the WHOLE insert), then INSERTs each row into
 *     `public_sales_allocation_scs` (is_status = 0). All-or-nothing.
 *
 * Access `btnSave_Click` validation (one error/row, checked only when present):
 *   - Contract No present but not in `mv_local_s`      → "This contract is not existed !"
 *   - DO No present but not in `v_delivery_order_scs`  → "This DO is not existed !"
 *   - Seal No duplicate in `mv_sales_allocation_scs`   → "This Seal No is existed !"
 *     (the seal check runs ONLY when Contract No is present).
 *
 * from/to warehouse are auto-derived by the backend from the DO
 * (`delivery_order_scs.from_warehouse` / `place_of_delivery`, is_status=0) when
 * the cell is left blank; a typed value overrides. So the cells are optional
 * overrides only (matches Access `btnSave` DLookup + Form_BeforeRender).
 *
 * Grid columns = temp-table fields in Access INSERT order:
 *   Contract No, DO No, M Date, M Truck No, From Warehouse, To Warehouse,
 *   M Net Weight, Container No, Seal No, No Bag, Tare Weight, Tare Carton Pallet,
 *   Tare Cont, Max Gross Weight, Allocating Bag, Allocating Quantity.
 *   (No column is required — Access has no `strCheck`.)
 *
 * UI/UX rebuilt to the Inbound Logistics › Goods Allocation Mass Upload style
 * (itself modelled on Futures Orders › Mass upload): a compact Excel-like grid —
 * paste TSV DIRECTLY into a cell (onPasteCapture, auto-grows rows to the pasted
 * block) + checkbox row select + Add row / Copy / Paste as new row(s) / Delete /
 * Reset, with Save/Cross-check/Cancel in the dialog header.
 *
 * Combo cells (Contract No / DO No / From & To Warehouse) are strict
 * Autocompletes — the matching Access single Add form `frmCtrlSalesQrySaleAlcAdd`
 * has these four as ComboBox with `LimitToList=True`, so only a value from the
 * list is accepted; an off-list typed/pasted value is dropped. Existence is still
 * re-validated server-side at Cross-check / Upload (race-safe).
 *
 * A per-row Cross-check (dry-run) surfaces the Access verdicts (✓/✗) before
 * Upload; Upload re-validates server-side (race-safe) and inserts all-or-nothing.
 * UI copy is English only and never reveals Access internals.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ClipboardEvent as ReactClipboardEvent } from 'react'

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import AddIcon from '@mui/icons-material/Add'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import ContentPasteIcon from '@mui/icons-material/ContentPaste'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Popper,
  type PopperProps,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import type { SxProps, Theme } from '@mui/material/styles'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'

import { useSnackbar } from '../../hooks/useSnackbar'
import { getErrorMessage } from '../../utils/error'
import {
  type SaleAlcMassLookupItem,
  type SaleAlcMassRow,
  useBulkCreateSaleAlcMassMutation,
  useSaleAlcLookupDeliveryOrdersQuery,
  useSaleAlcMassContractsQuery,
  useSaleAlcMassDeliveryOrdersQuery,
  useSaleAlcMassWarehousesQuery,
  useValidateSaleAlcMassMutation,
} from '../../store/api/saleControlApi'

// ── Compact grid look (đồng bộ Goods Allocation / Futures Orders › Mass upload) ──
const massCellSx = { px: 0.5, py: 0.25 }
// Height CỐ ĐỊNH cho mọi field (TextField / date / Autocomplete) → cùng chiều cao.
const MASS_FIELD_H = 32
const massInputSx: SxProps<Theme> = {
  '& .MuiInputBase-root': {
    height: MASS_FIELD_H,
    minHeight: MASS_FIELD_H,
    fontSize: '0.72rem',
    py: 0,
    boxSizing: 'border-box',
  },
  '& .MuiInputBase-input': { py: 0, fontSize: '0.72rem' },
  '& .MuiAutocomplete-input': { py: '0 !important', fontSize: '0.72rem' },
}
const massHeaderSx: SxProps<Theme> = {
  fontWeight: 700,
  whiteSpace: 'nowrap',
  fontSize: '0.7rem',
  lineHeight: 1.2,
  py: 0.5,
  px: 0.75,
  bgcolor: '#fafafa',
}
const toolbarBtnSx: SxProps<Theme> = {
  fontSize: '0.7rem',
  py: 0.25,
  px: 0.875,
  minHeight: 26,
  textTransform: 'none',
}

// ── Dialog chrome — values replicated inline from Futures Orders › Mass upload
//    (the canonical template); NOT imported from the futures module. ──
const massDialogPaperSx: SxProps<Theme> = {
  maxHeight: 'min(900px, 92vh)',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: 1,
}
const massDialogTitleSx: SxProps<Theme> = {
  pb: 1,
  pt: 1.25,
  px: 2,
  display: 'flex',
  alignItems: 'center',
  flexShrink: 0,
  fontWeight: 700,
  fontSize: '1rem',
  lineHeight: 1.2,
}
const massTitleActionsBoxSx: SxProps<Theme> = {
  display: 'flex',
  gap: 0.5,
  alignItems: 'center',
  ml: 'auto',
  flexShrink: 0,
}
const massTitleBtnSx: SxProps<Theme> = {
  py: 0.25,
  px: 1,
  minHeight: 28,
  fontSize: '0.75rem',
  lineHeight: 1.2,
  textTransform: 'none',
}
const massDialogContentSx: SxProps<Theme> = {
  pt: 0.75,
  px: 2,
  pb: 1.5,
  overflowY: 'auto',
  flex: '1 1 auto',
  minHeight: 0,
  bgcolor: '#FFFFFF',
  '& .MuiInputBase-root': { minHeight: 32, fontSize: '0.8125rem' },
  '& .MuiInputBase-input': { py: 0.5, fontSize: '0.8125rem' },
  '& .MuiInputLabel-root': { fontSize: '0.8125rem', fontWeight: 500 },
  '& .MuiInputLabel-shrink': { fontWeight: 600 },
  '& .MuiFormControlLabel-label': { fontSize: '0.8125rem', fontWeight: 500 },
  '& .MuiFormHelperText-root': { fontSize: '0.6875rem' },
  '& .MuiTypography-caption': {
    fontSize: '0.6875rem',
    fontWeight: 600,
    letterSpacing: '0.03em',
    textTransform: 'uppercase',
  },
  '& .MuiTypography-body2': { fontSize: '0.8125rem' },
  '& .MuiSelect-select': { fontSize: '0.8125rem' },
  '& .MuiOutlinedInput-root': { borderRadius: 1 },
}

/** Editable grid row (all cells string while editing). */
interface MassRow {
  sale_contract: string
  delivery_order: string
  m_date: string // ISO YYYY-MM-DD ('' = blank)
  m_truck_no: string
  from_warehouse: string
  to_warehouse: string
  m_net_weight: string
  container_no: string
  seal_no: string
  number_bags: string
  tare_weight: string
  tare_carton_pallet: string
  tare_cont: string
  max_gross_weight: string
  allocating_bag: string
  allocating_quantity: string
}

type FieldKey = keyof MassRow
// 'combo' = Access ComboBox with LimitToList=True → strict Autocomplete, only a
// value from the list is accepted (typed/pasted off-list values are dropped);
// 'text'/'date'/'number' are plain free-text cells.
type FieldKind = 'text' | 'combo' | 'date' | 'number'

const FIELDS: Array<{ key: FieldKey; label: string; kind: FieldKind; width: number }> = [
  { key: 'sale_contract', label: 'Contract No', kind: 'combo', width: 170 },
  { key: 'delivery_order', label: 'DO No', kind: 'combo', width: 130 },
  { key: 'm_date', label: 'M Date', kind: 'date', width: 140 },
  { key: 'm_truck_no', label: 'M Truck No', kind: 'text', width: 120 },
  { key: 'from_warehouse', label: 'From Warehouse', kind: 'combo', width: 160 },
  { key: 'to_warehouse', label: 'To Warehouse', kind: 'combo', width: 160 },
  { key: 'm_net_weight', label: 'M Net Weight', kind: 'number', width: 120 },
  { key: 'container_no', label: 'Container No', kind: 'text', width: 130 },
  { key: 'seal_no', label: 'Seal No', kind: 'text', width: 120 },
  { key: 'number_bags', label: 'No Bag', kind: 'number', width: 100 },
  { key: 'tare_weight', label: 'Tare Weight', kind: 'number', width: 110 },
  { key: 'tare_carton_pallet', label: 'Tare Carton Pallet', kind: 'number', width: 130 },
  { key: 'tare_cont', label: 'Tare Cont', kind: 'number', width: 110 },
  { key: 'max_gross_weight', label: 'Max Gross Weight', kind: 'number', width: 130 },
  { key: 'allocating_bag', label: 'Allocating Bag', kind: 'number', width: 110 },
  { key: 'allocating_quantity', label: 'Allocating Quantity', kind: 'number', width: 130 },
]

let ROW_SEQ = 0
function emptyRow(): { id: number; data: MassRow } {
  const data: MassRow = {
    sale_contract: '',
    delivery_order: '',
    m_date: '',
    m_truck_no: '',
    from_warehouse: '',
    to_warehouse: '',
    m_net_weight: '',
    container_no: '',
    seal_no: '',
    number_bags: '',
    tare_weight: '',
    tare_carton_pallet: '',
    tare_cont: '',
    max_gross_weight: '',
    allocating_bag: '',
    allocating_quantity: '',
  }
  return { id: ++ROW_SEQ, data }
}

/** Verdict for one row after cross-check (keyed by original grid index). */
interface RowVerdict {
  ok: boolean
  messages: string[]
  badFields: Set<FieldKey>
}

/** Strip thousand-sep + spaces → numeric string ('' if not parseable). */
function normNumber(raw: string): string {
  const cleaned = raw.replace(/,/g, '').replace(/\s+/g, '').trim()
  if (cleaned === '') return ''
  const n = Number(cleaned)
  return Number.isFinite(n) ? String(n) : ''
}

/** Access Format Standard `#,##0.00` — '' for null/blank. */
function formatStandard(v: number | null | undefined): string {
  if (v === null || v === undefined) return ''
  return v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function toNum(v: string): number | null {
  const s = v.replace(/,/g, '').trim()
  if (!s) return null
  const n = Number(s)
  return Number.isFinite(n) ? n : null
}

/** "a\tb\nc\td" → [['a','b'],['c','d']] (paste at a cell → grows to the block). */
function parseTsv(text: string): string[][] {
  const trimmed = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n').replace(/\n$/, '')
  if (!trimmed) return []
  return trimmed.split('\n').map((line) => line.split('\t'))
}

/** Chỉ feed YYYY-MM-DD hợp lệ cho DatePicker (tránh giá trị rác). */
function isoOrEmpty(v: string): string {
  return /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/.test(v) ? v : ''
}

/** Dòng có nhập ít nhất 1 ô? (bỏ dòng hoàn toàn trống). */
function isRowFilled(data: MassRow): boolean {
  return FIELDS.some((f) => (data[f.key] ?? '').trim() !== '')
}

/** Client-side per-row checks mirroring the single New/Edit/Copy form's Save gate
 *  (`SalesAllocationFormDialog` canSave / Access `frmCtrlSalesQrySaleAlcAdd` strCheck)
 *  that the BE dry-run (`_validate_rows`) does NOT cover — its checks are all
 *  conditional-on-present (contract/DO existence, seal dup) with NO required-field
 *  check, so an empty Contract No passes the BE. These are merged into each row
 *  verdict (row ok = BE-ok AND client-ok) so ✓/✗ + the Upload gate match the single
 *  form. Rules for fields absent from the mass grid (consignment → errCheck 2,
 *  receipt/virtual → errCheck 3) or the auto-gen PK are out of scope. */
function clientRowErrors(r: MassRow): Array<{ field: FieldKey; message: string }> {
  const errs: Array<{ field: FieldKey; message: string }> = []
  // Single form errCheck 1 (contractMissing): a contract is mandatory. The mass grid
  // exposes only the Contract No column, so require it to be present.
  if (!r.sale_contract.trim()) errs.push({ field: 'sale_contract', message: 'Contract No is required' })
  return errs
}

/** A pasted row is a HEADER row (not data) when its leading cells match our column
 *  titles (case-insensitive). Require ≥2 matches so a genuine data row whose first
 *  cell happens to read "Contract No" isn't dropped. Mirrors Futures `isMassHeaderRow`. */
function isMassHeaderRow(cells: string[]): boolean {
  const expected = FIELDS.map((f) => f.label.toLowerCase())
  let matches = 0
  cells.forEach((c, i) => {
    if (i < expected.length && c.trim().toLowerCase() === expected[i]) matches += 1
  })
  return matches >= 2
}

/** Popper cho combo cell: dropdown rộng theo nội dung (value dài đọc đủ). */
function WideComboPopper(props: PopperProps) {
  return (
    <Popper
      {...props}
      placement="bottom-start"
      style={{ ...props.style, width: 'fit-content', minWidth: 240, maxWidth: 520 }}
    />
  )
}

interface Props {
  open: boolean
  canEdit: boolean
  onClose: () => void
  /** Fired after a successful upload (parent may refetch / close). */
  onUploaded?: () => void
}

export default function SalesAllocationMassUploadDialog({ open, canEdit, onClose, onUploaded }: Props) {
  const snack = useSnackbar()
  const [rows, setRows] = useState<{ id: number; data: MassRow }[]>([emptyRow()])
  const [selected, setSelected] = useState<Set<number>>(() => new Set())
  // Two-layer cross-check (canonical = Futures Orders › Mass upload):
  //  1. INSTANT client layer — `clientVerdictByRow` is a useMemo over `rows` that
  //     recomputes synchronously on every keystroke/paste (never blanked). It drives
  //     ✓/✗, red cells, the Alert and the caption immediately, exactly like Futures
  //     `massVerdicts`.
  //  2. DEBOUNCED BE layer — the dry-run for things only the server can check
  //     (existence / seal dup). Stored in its own `beVerdictByRow` keyed by row index;
  //     `bePending` marks the BE result STALE while a re-validate is pending (the
  //     client verdict is NOT blanked). `autoSeqRef` guards out-of-order responses.
  const [beVerdictByRow, setBeVerdictByRow] = useState<Record<number, RowVerdict>>({})
  const [bePending, setBePending] = useState(false)
  const autoSeqRef = useRef(0)

  // Grid combos — free-text suggestions only (typed/pasted value never dropped).
  // Skip until open; guard with `?? []`. The Access header Client combo (`cp_vn`)
  // is hidden (`Visible = NotDefault`) so it is not rendered / does not scope.
  const contractsQuery = useSaleAlcMassContractsQuery(undefined, { skip: !open })
  const contractOptions = contractsQuery.data?.items ?? []
  // Flat (all-DO) list — kept ONLY for synchronous PASTE coercion (`optionsForKind`
  // → `coerceCell`), which runs at dialog level with no per-row contract in hand. The
  // live DO combo options are contract-scoped per row (see `DoCell`), and any pasted
  // off-contract DO is caught by the BE dry-run / pre-insert re-validate.
  const doQuery = useSaleAlcMassDeliveryOrdersQuery(undefined, { skip: !open })
  const doOptions = doQuery.data?.items ?? []
  const warehousesQuery = useSaleAlcMassWarehousesQuery(undefined, { skip: !open })
  const warehouseOptions = warehousesQuery.data?.items ?? []

  const [validateMass, { isLoading: checking }] = useValidateSaleAlcMassMutation()
  const [bulkCreate, { isLoading: uploading }] = useBulkCreateSaleAlcMassMutation()

  const filledCount = useMemo(() => rows.filter((r) => isRowFilled(r.data)).length, [rows])
  const allSelected = rows.length > 0 && selected.size === rows.length

  // ── LAYER 1: INSTANT client verdict (never blanked) ────────────────────────
  // Mirrors Futures/Lot `massVerdicts`: a synchronous useMemo over EVERY row that runs
  // the EXISTING `clientRowErrors` rules on every edit/paste. ALL rows are validated
  // (incl. the empty starter row → it fails "Contract No is required"), so on open the
  // grid reads "Cross-check: 1 of 1 row(s) need fixing" exactly like the other 4 forms.
  // Keyed by original grid index.
  const clientVerdictByRow = useMemo(() => {
    const map: Record<number, RowVerdict> = {}
    rows.forEach((r, i) => {
      const errs = clientRowErrors(r.data)
      map[i] = {
        ok: errs.length === 0,
        messages: errs.map((e) => e.message),
        badFields: new Set(errs.map((e) => e.field)),
      }
    })
    return map
  }, [rows])
  const clientAllOk = Object.values(clientVerdictByRow).every((v) => v.ok)

  // ── Merge (client ⊕ be) for DISPLAY ────────────────────────────────────────
  // A row is ✗ if the client verdict fails OR (a BE verdict is present and fails).
  // Messages + badFields are unioned so the icon tooltip, red cells and the Alert
  // show every reason at once.
  const mergedVerdictByRow = useMemo(() => {
    const map: Record<number, RowVerdict> = {}
    rows.forEach((_, i) => {
      const c = clientVerdictByRow[i]
      const b = beVerdictByRow[i]
      if (!c && !b) return
      const badFields = new Set<FieldKey>()
      c?.badFields.forEach((f) => badFields.add(f))
      b?.badFields.forEach((f) => badFields.add(f))
      map[i] = {
        ok: (c ? c.ok : true) && (b ? b.ok : true),
        messages: [...(c?.messages ?? []), ...(b?.messages ?? [])],
        badFields,
      }
    })
    return map
  }, [rows, clientVerdictByRow, beVerdictByRow])

  const beAllOk =
    Object.keys(beVerdictByRow).length > 0 && Object.values(beVerdictByRow).every((v) => v.ok)
  const anyError = Object.values(mergedVerdictByRow).some((v) => !v.ok)
  const failCount = Object.values(mergedVerdictByRow).filter((v) => !v.ok).length

  const optionsForKind = (key: FieldKey): SaleAlcMassLookupItem[] => {
    if (key === 'sale_contract') return contractOptions
    if (key === 'delivery_order') return doOptions
    if (key === 'from_warehouse' || key === 'to_warehouse') return warehouseOptions
    return []
  }

  const reset = () => {
    setRows([emptyRow()])
    setSelected(new Set())
    setBeVerdictByRow({})
    setBePending(false)
  }

  const handleClose = () => {
    if (uploading) return
    onClose()
  }

  const setCell = (rowIdx: number, field: FieldKey, value: string) => {
    setRows((cur) => cur.map((r, i) => (i === rowIdx ? { ...r, data: { ...r.data, [field]: value } } : r)))
  }

  /** Format Standard (thousand sep + 2dp) khi blur ô số — giống form New. */
  const handleNumericBlur = (rowIdx: number, field: FieldKey) => () =>
    setRows((cur) =>
      cur.map((r, i) =>
        i === rowIdx ? { ...r, data: { ...r.data, [field]: (() => {
          const n = toNum(r.data[field])
          return n == null ? '' : formatStandard(n)
        })() } } : r,
      ),
    )

  /** Chuẩn hoá 1 ô dán theo loại cột. Combo (Access ComboBox `LimitToList=True`) →
   *  phải khớp value trong list (case-insensitive), không khớp thì BỎ (không nhận
   *  giá trị ngoài list); date → ISO (sai format → rỗng); number → Format Standard;
   *  text → giữ nguyên. */
  const coerceCell = (field: (typeof FIELDS)[number], raw: string): string => {
    const v = (raw ?? '').replace(/\r$/, '')
    if (field.kind === 'combo') {
      const t = v.trim()
      if (!t) return ''
      const hit = optionsForKind(field.key).find((o) => String(o.value).toLowerCase() === t.toLowerCase())
      return hit ? String(hit.value) : ''
    }
    if (field.kind === 'date') {
      const d = dayjs(v.trim())
      return d.isValid() ? d.format('YYYY-MM-DD') : ''
    }
    if (field.kind === 'number') {
      const s = normNumber(v)
      return s === '' ? '' : formatStandard(Number(s))
    }
    return v.trim()
  }

  /** Dán tại ô (rowIdx,colIdx) — nở dòng theo vùng dán, điền theo thứ tự cột. */
  const handlePaste = (rowIdx: number, colIdx: number, e: ReactClipboardEvent<HTMLElement>) => {
    const text = e.clipboardData?.getData('text') ?? ''
    // Ô đơn (không tab/newline) → để input tự xử lý paste bình thường.
    if (!text || (!text.includes('\t') && !text.includes('\n'))) return
    e.preventDefault()
    e.stopPropagation()
    const matrix = parseTsv(text)
    if (!matrix.length) return
    setRows((cur) => {
      const next = cur.map((r) => ({ id: r.id, data: { ...r.data } }))
      matrix.forEach((line, r) => {
        const target = rowIdx + r
        while (next.length <= target) next.push(emptyRow())
        line.forEach((cellRaw, c) => {
          const field = FIELDS[colIdx + c]
          if (!field) return
          next[target].data[field.key] = coerceCell(field, cellRaw)
        })
      })
      return next
    })
  }

  const addRow = () => {
    setRows((cur) => [...cur, emptyRow()])
  }

  const toggleRow = (idx: number) =>
    setSelected((cur) => {
      const next = new Set(cur)
      if (next.has(idx)) next.delete(idx)
      else next.add(idx)
      return next
    })

  const toggleAll = () =>
    setSelected((cur) => (cur.size === rows.length && rows.length > 0 ? new Set() : new Set(rows.map((_, i) => i))))

  const deleteSelected = () => {
    if (selected.size === 0) return
    setRows((cur) => {
      const kept = cur.filter((_, i) => !selected.has(i))
      return kept.length ? kept : [emptyRow()]
    })
    setSelected(new Set())
  }

  /** Delete a single row (trailing delete-row button) — mirrors Futures Orders:
   *  drop the row + re-index the selection set. */
  const deleteRow = (index: number) => {
    setRows((cur) => {
      const kept = cur.filter((_, i) => i !== index)
      return kept.length ? kept : [emptyRow()]
    })
    setSelected((cur) => {
      const next = new Set<number>()
      cur.forEach((i) => {
        if (i === index) return
        next.add(i > index ? i - 1 : i)
      })
      return next
    })
  }

  /** Copy bằng Selection API trên div contenteditable ẩn (không dùng textarea.focus()
   *  — MUI Dialog FocusTrap giữ focus khiến execCommand copy rỗng). Chạy đúng cả trên
   *  origin http nơi navigator.clipboard undefined. */
  const copyViaSelection = (html: string): boolean => {
    const div = document.createElement('div')
    div.contentEditable = 'true'
    div.innerHTML = html
    div.style.position = 'fixed'
    div.style.top = '-9999px'
    div.style.left = '-9999px'
    div.style.opacity = '0'
    document.body.appendChild(div)
    const selection = window.getSelection()
    if (!selection) {
      document.body.removeChild(div)
      return false
    }
    const savedRanges: Range[] = []
    for (let i = 0; i < selection.rangeCount; i++) savedRanges.push(selection.getRangeAt(i).cloneRange())
    const range = document.createRange()
    range.selectNodeContents(div)
    selection.removeAllRanges()
    selection.addRange(range)
    let ok = false
    try {
      ok = document.execCommand('copy')
    } catch {
      ok = false
    }
    selection.removeAllRanges()
    savedRanges.forEach((r) => selection.addRange(r))
    document.body.removeChild(div)
    return ok
  }

  const copyViaTextarea = (text: string): boolean => {
    const ta = document.createElement('textarea')
    ta.value = text
    ta.setAttribute('readonly', '')
    ta.style.position = 'fixed'
    ta.style.top = '-9999px'
    document.body.appendChild(ta)
    const prevActive = document.activeElement as HTMLElement | null
    ta.focus()
    ta.select()
    let ok = false
    try {
      ok = document.execCommand('copy')
    } catch {
      ok = false
    }
    document.body.removeChild(ta)
    prevActive?.focus?.()
    return ok
  }

  /** Copy dòng đã chọn (hoặc toàn bộ) ra clipboard hệ thống dạng TSV+HTML — cả hai
   *  đều dẫn đầu bằng 1 hàng header (Ctrl+V sang Excel đúng cột; dán lại vào web bỏ header). */
  const copyRows = async () => {
    const idx = selected.size > 0 ? [...selected].sort((a, b) => a - b) : rows.map((_, i) => i)
    const cellText = (i: number, key: FieldKey) => String(rows[i].data[key] ?? '').replace(/[\t\r\n]/g, ' ')
    const headerRow = FIELDS.map((f) => f.label).join('\t')
    const tsv = [headerRow, ...idx.map((i) => FIELDS.map((f) => cellText(i, f.key)).join('\t'))].join('\n')
    const escapeHtml = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    const html =
      '<table><tr>' +
      FIELDS.map((f) => `<th>${escapeHtml(f.label)}</th>`).join('') +
      '</tr>' +
      idx.map((i) => '<tr>' + FIELDS.map((f) => `<td>${escapeHtml(cellText(i, f.key))}</td>`).join('') + '</tr>').join('') +
      '</table>'

    let ok = false
    try {
      const ClipboardItemCtor = (window as unknown as { ClipboardItem?: typeof ClipboardItem }).ClipboardItem
      if (ClipboardItemCtor && navigator.clipboard?.write) {
        const item = new ClipboardItemCtor({
          'text/plain': new Blob([tsv], { type: 'text/plain' }),
          'text/html': new Blob([html], { type: 'text/html' }),
        })
        await navigator.clipboard.write([item])
        ok = true
      }
    } catch {
      ok = false
    }
    if (!ok) ok = copyViaSelection(html)
    if (!ok) {
      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(tsv)
          ok = true
        }
      } catch {
        ok = false
      }
    }
    if (!ok) ok = copyViaTextarea(tsv)

    if (ok) snack.success(`Copied ${idx.length} row${idx.length === 1 ? '' : 's'} — paste into the grid or Excel.`)
    else snack.error('Browser blocked clipboard access. Open the page over HTTPS or localhost.')
  }

  /** Append a TSV block (from the SYSTEM clipboard / Excel, or our own Copy output)
   *  as brand-new rows at the END of the grid. Drops a leading header row and, if the
   *  grid is still the untouched starter row, replaces it. Coerces every cell exactly
   *  like the per-cell paste (`coerceCell`). Returns rows added. Mirrors Futures
   *  `appendMassRowsFromText`. */
  const appendRowsFromText = useCallback(
    (text: string): number => {
      const matrix = parseTsv(text)
      if (!matrix.length) return 0
      const dataRows = matrix.length > 1 && isMassHeaderRow(matrix[0]) ? matrix.slice(1) : matrix
      if (!dataRows.length) return 0
      const built = dataRows.map((cells) => {
        const { data } = emptyRow()
        cells.forEach((raw, c) => {
          const field = FIELDS[c]
          if (!field) return
          data[field.key] = coerceCell(field, raw)
        })
        return data
      })
      setRows((cur) => {
        const pristine = cur.length === 1 && !isRowFilled(cur[0].data)
        const appended = built.map((data) => ({ id: ++ROW_SEQ, data }))
        return pristine ? appended : [...cur, ...appended]
      })
      return built.length
    },
    // coerceCell closes over the lookup option arrays — recreate when they load.
    [contractOptions, doOptions, warehouseOptions],
  )

  // While the dialog is open, a Ctrl+V that isn't aimed at an editable input (grid
  // cells handle their own paste and stop propagation) → paste the SYSTEM clipboard
  // (Excel) as brand-new rows. This is the reliable path on plain-HTTP origins where
  // the async Clipboard read API is unavailable and only a real paste gesture exposes
  // clipboard data. Per-cell paste (auto-grow) is untouched.
  useEffect(() => {
    if (!open) return
    const onDocPaste = (e: ClipboardEvent) => {
      const target = e.target as HTMLElement | null
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) {
        return
      }
      const text = e.clipboardData?.getData('text') ?? ''
      if (!text.trim()) return
      e.preventDefault()
      const added = appendRowsFromText(text)
      if (added > 0) snack.success(`Pasted ${added} new row${added === 1 ? '' : 's'}`)
    }
    document.addEventListener('paste', onDocPaste)
    return () => document.removeEventListener('paste', onDocPaste)
  }, [open, appendRowsFromText, snack])

  // Filled rows (with original grid index) — skip fully-empty rows.
  const getFilled = () => rows.map((r, i) => ({ r: r.data, i })).filter(({ r }) => isRowFilled(r))

  const buildPayload = (filled: { r: MassRow }[]): SaleAlcMassRow[] =>
    filled.map(({ r }) => ({
      sale_contract: r.sale_contract.trim(),
      delivery_order: r.delivery_order.trim(),
      m_date: r.m_date.trim() || null,
      m_truck_no: r.m_truck_no.trim(),
      from_warehouse: r.from_warehouse.trim(),
      to_warehouse: r.to_warehouse.trim(),
      m_net_weight: toNum(r.m_net_weight),
      container_no: r.container_no.trim(),
      seal_no: r.seal_no.trim(),
      number_bags: toNum(r.number_bags),
      tare_weight: toNum(r.tare_weight),
      tare_carton_pallet: toNum(r.tare_carton_pallet),
      tare_cont: toNum(r.tare_cont),
      max_gross_weight: toNum(r.max_gross_weight),
      allocating_bag: toNum(r.allocating_bag),
      allocating_quantity: toNum(r.allocating_quantity),
    }))

  // LAYER 2 — BE dry-run (existence / seal dup only; things the client cannot check).
  // Stores its verdict in `beVerdictByRow` keyed by original index. It does NOT fold
  // in the client rules (those live in the instant layer) and NEVER blanks the client
  // verdict. Bumps `autoSeqRef` so any in-flight (older) response is treated as stale.
  // Returns true if every filled row is BE-ok.
  const runBeValidate = useCallback(async (): Promise<boolean> => {
    const filled = rows.map((r, i) => ({ r: r.data, i })).filter(({ r }) => isRowFilled(r))
    const seq = ++autoSeqRef.current
    if (filled.length === 0) {
      setBeVerdictByRow({})
      setBePending(false)
      return false
    }
    setBePending(true)
    try {
      const res = await validateMass({ rows: buildPayload(filled) }).unwrap()
      if (seq !== autoSeqRef.current) return false // a newer edit/validate superseded us
      const map: Record<number, RowVerdict> = {}
      res.rows.forEach((v, k) => {
        const origIdx = filled[k]?.i
        if (origIdx == null) return
        map[origIdx] = {
          ok: v.ok,
          messages: v.errors.map((e) => e.message),
          badFields: new Set(v.errors.map((e) => e.field).filter((f): f is FieldKey => !!f)),
        }
      })
      setBeVerdictByRow(map)
      setBePending(false)
      return Object.values(map).every((v) => v.ok)
    } catch (err) {
      if (seq !== autoSeqRef.current) return false
      setBePending(false)
      return false
    }
  }, [rows, validateMass])

  // Debounced BE cross-check: on every grid change, wait ~450ms then run the dry-run —
  // but ONLY when there is ≥1 filled row AND the instant client layer has NO failures
  // (skip the BE while client rows are still failing; they are already ✗). `runBeValidate`
  // changes identity with `rows`, so the effect re-arms (cancelling the previous timer)
  // on every edit — that IS the debounce. The client verdict is never blanked here.
  useEffect(() => {
    if (!open) return
    if (filledCount === 0 || !clientAllOk) {
      autoSeqRef.current++ // invalidate any in-flight response
      setBePending(false)
      setBeVerdictByRow((v) => (Object.keys(v).length ? {} : v))
      return
    }
    setBePending(true)
    const timer = window.setTimeout(() => {
      void runBeValidate()
    }, 450)
    return () => window.clearTimeout(timer)
  }, [open, filledCount, clientAllOk, runBeValidate])

  // Upload = re-validate (race-safe) then a single bulk insert. Client rules gate first
  // (instant), then the BE dry-run confirms existence/seal right before the insert.
  const runUpload = async () => {
    if (!canEdit) return
    const filled = getFilled()
    if (filled.length === 0) {
      snack.warning('At least 1 row is required')
      return
    }
    const clientBad = filled.findIndex(({ r }) => clientRowErrors(r).length > 0)
    if (clientBad >= 0) {
      snack.warning(clientRowErrors(filled[clientBad].r)[0]?.message ?? 'Fix highlighted rows')
      return
    }
    const ok = await runBeValidate()
    if (!ok) return
    try {
      const res = await bulkCreate({ rows: buildPayload(filled) }).unwrap()
      snack.success(`Uploaded ${res.created}/${filled.length} rows`)
      reset()
      onUploaded?.()
      onClose()
    } catch (err) {
      snack.error(getErrorMessage(err) || 'Could not upload rows')
      void runBeValidate()
    }
  }

  return (
    <Dialog
      open={open}
      onClose={(_e, reason) => {
        if (uploading || reason === 'backdropClick') return
        handleClose()
      }}
      maxWidth="xl"
      fullWidth
      scroll="paper"
      PaperProps={{ sx: massDialogPaperSx }}
    >
      <DialogTitle sx={massDialogTitleSx}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ width: '100%' }}>
          <Box component="span">Mass Upload — Sales Allocation</Box>
          <Box sx={massTitleActionsBoxSx}>
            <Tooltip title={failCount > 0 ? `${failCount} row(s) failed cross-check — fix to enable Upload` : ''}>
              <span>
                <Button
                  type="button"
                  variant="contained"
                  size="small"
                  color="success"
                  disabled={!canEdit || uploading || checking || bePending || !clientAllOk || !beAllOk || filledCount === 0}
                  onClick={() => void runUpload()}
                  sx={massTitleBtnSx}
                >
                  {uploading ? <CircularProgress size={14} color="inherit" /> : 'Upload'}
                </Button>
              </span>
            </Tooltip>
            <Button
              type="button"
              variant="outlined"
              size="small"
              onClick={handleClose}
              disabled={uploading}
              sx={massTitleBtnSx}
            >
              Cancel
            </Button>
          </Box>
        </Stack>
      </DialogTitle>

      <DialogContent dividers sx={massDialogContentSx}>
        <Stack spacing={1}>
          <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center" flexWrap="wrap">
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                textTransform: 'none !important',
                letterSpacing: 'normal',
                color: bePending || checking ? 'text.secondary' : failCount > 0 ? 'error.main' : 'success.main',
              }}
            >
              {bePending || checking
                ? 'Cross-checking…'
                : failCount > 0
                  ? `Cross-check: ${failCount} of ${rows.length} row(s) need fixing`
                  : `Cross-check passed — ${rows.length} row(s) ready`}
            </Typography>
            <Stack direction="row" spacing={0.75}>
              <Button size="small" variant="outlined" startIcon={<AddIcon sx={{ fontSize: 14 }} />} onClick={addRow} disabled={!canEdit} sx={toolbarBtnSx}>
                Add row
              </Button>
              <Button size="small" variant="outlined" startIcon={<ContentCopyIcon sx={{ fontSize: 14 }} />} onClick={() => void copyRows()} sx={toolbarBtnSx}>
                {selected.size > 0 ? `Copy (${selected.size})` : 'Copy all'}
              </Button>
              <Button size="small" variant="outlined" color="error" startIcon={<DeleteOutlineIcon sx={{ fontSize: 14 }} />} disabled={!canEdit || selected.size === 0} onClick={deleteSelected} sx={toolbarBtnSx}>
                {selected.size > 0 ? `Delete (${selected.size})` : 'Delete selected'}
              </Button>
              <Button size="small" variant="text" onClick={reset} disabled={!canEdit} sx={toolbarBtnSx}>
                Reset
              </Button>
            </Stack>
          </Stack>

          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              textTransform: 'none !important',
              letterSpacing: 'normal',
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            <ContentPasteIcon sx={{ fontSize: 14 }} />
            Copy rows in Excel (or click Copy), then press Ctrl+V to paste them as new rows. You can also paste
            directly into a cell.
          </Typography>

          <Box sx={{ maxHeight: '55vh', overflow: 'auto', border: '1px solid #bdbdbd', bgcolor: '#fff' }}>
            <Table size="small" stickyHeader sx={{ width: '100%', tableLayout: 'fixed' }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...massHeaderSx, width: 44 }} />
                  <TableCell padding="checkbox" align="left" sx={{ bgcolor: '#fafafa', width: 44, pl: 0.75, pr: 2 }}>
                    <Checkbox
                      size="small"
                      indeterminate={selected.size > 0 && !allSelected}
                      checked={allSelected}
                      onChange={toggleAll}
                      disabled={!canEdit}
                      sx={{ p: 0.25, ml: 0 }}
                    />
                  </TableCell>
                  <TableCell sx={{ ...massHeaderSx, width: 36, textAlign: 'center', px: 0.25 }} title="Cross-check status">✓</TableCell>
                  {FIELDS.map((f) => (
                    <TableCell key={f.key} sx={{ ...massHeaderSx, width: f.width }}>
                      {f.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r, index) => {
                  const isSel = selected.has(index)
                  const verdict = mergedVerdictByRow[index]
                  const failed = !!verdict && !verdict.ok
                  const cellBg = (key?: FieldKey): string | undefined =>
                    failed ? (key && verdict!.badFields.has(key) ? 'rgba(211,47,47,0.24)' : 'rgba(211,47,47,0.06)') : undefined
                  return (
                    <TableRow key={r.id} hover selected={isSel}>
                      <TableCell sx={{ ...massCellSx, width: 44, verticalAlign: 'middle', bgcolor: cellBg() }}>
                        <Tooltip title="Delete row">
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              disabled={!canEdit || rows.length === 1}
                              onClick={() => deleteRow(index)}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                      <TableCell padding="checkbox" align="left" sx={{ width: 44, pl: 0.75, pr: 2, bgcolor: isSel ? undefined : cellBg() }}>
                        <Checkbox size="small" checked={isSel} onChange={() => toggleRow(index)} disabled={!canEdit} sx={{ p: 0.25, ml: 0 }} />
                      </TableCell>
                      <TableCell sx={{ ...massCellSx, width: 36, textAlign: 'center', verticalAlign: 'middle', px: 0.25, bgcolor: cellBg() }}>
                        {verdict ? (
                          verdict.ok ? (
                            <CheckCircleOutlineIcon sx={{ fontSize: 17, color: 'success.main', verticalAlign: 'middle' }} />
                          ) : (
                            <Tooltip title={<Box sx={{ whiteSpace: 'pre-line' }}>{verdict.messages.join('\n')}</Box>}>
                              <ErrorOutlineIcon sx={{ fontSize: 17, color: 'error.main', verticalAlign: 'middle' }} />
                            </Tooltip>
                          )
                        ) : null}
                      </TableCell>
                      {FIELDS.map((f, colIdx) => (
                        <TableCell key={f.key} sx={{ ...massCellSx, bgcolor: cellBg(f.key) }} onPasteCapture={(e) => handlePaste(index, colIdx, e)}>
                          {f.kind === 'combo' ? (
                            f.key === 'delivery_order' ? (
                              <DoCell
                                rowContract={r.data.sale_contract}
                                value={r.data.delivery_order}
                                onChange={(v) => setCell(index, 'delivery_order', v)}
                                disabled={!canEdit}
                              />
                            ) : (
                              <ComboCell
                                value={r.data[f.key]}
                                options={optionsForKind(f.key)}
                                onChange={(v) => setCell(index, f.key, v)}
                                disabled={!canEdit}
                              />
                            )
                          ) : f.kind === 'date' ? (
                            <DatePicker
                              format="DD-MMM-YYYY"
                              value={isoOrEmpty(r.data[f.key]) ? dayjs(r.data[f.key]) : null}
                              disabled={!canEdit}
                              onChange={(d) => setCell(index, f.key, d && d.isValid() ? d.format('YYYY-MM-DD') : '')}
                              slotProps={{ textField: { size: 'small', fullWidth: true, placeholder: '—', sx: massInputSx } }}
                            />
                          ) : (
                            <TextField
                              value={r.data[f.key]}
                              onChange={(e) =>
                                setCell(index, f.key, f.kind === 'number' ? e.target.value.replace(/[^\d.,-]/g, '') : e.target.value)
                              }
                              onBlur={f.kind === 'number' ? handleNumericBlur(index, f.key) : undefined}
                              disabled={!canEdit}
                              size="small"
                              fullWidth
                              placeholder="—"
                              inputProps={f.kind === 'number' ? { style: { textAlign: 'right' }, inputMode: 'decimal' } : undefined}
                              sx={massInputSx}
                            />
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </Box>

          {/* Cross-check error summary — visible, no hover needed. */}
          {anyError && (
            <Alert severity="warning" sx={{ py: 0.5, '& .MuiAlert-message': { py: 0.25 } }}>
              <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.25, textTransform: 'none !important', letterSpacing: 'normal' }}>
                Cross-check issues — fix before Upload
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2 }}>
                {Object.entries(mergedVerdictByRow)
                  .filter(([, v]) => !v.ok)
                  .sort((a, b) => Number(a[0]) - Number(b[0]))
                  .map(([idx, v]) => (
                    <Typography key={idx} component="li" variant="caption" sx={{ display: 'list-item', textTransform: 'none !important', letterSpacing: 'normal' }}>
                      {`Row ${Number(idx) + 1}: ${v.messages.join('; ')}`}
                    </Typography>
                  ))}
              </Box>
            </Alert>
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  )
}

/** Strict combobox cell — Access ComboBox with `LimitToList=True`: ONLY a value
 *  from the list is accepted; a typed/pasted off-list value is not kept. The
 *  current value is injected into the options so an already-set value still shows
 *  even if the lookup reloads. Field shows the code; dropdown shows "<CODE> — <NAME>". */
function ComboCell({
  value,
  options,
  disabled,
  onChange,
}: {
  value: string
  options: SaleAlcMassLookupItem[]
  disabled: boolean
  onChange: (value: string) => void
}) {
  const sel = options.find((o) => o.value === value) ?? (value ? { value, label: value } : undefined)
  const merged = sel && !options.some((o) => o.value === sel.value) ? [sel, ...options] : options
  return (
    <Autocomplete
      size="small"
      fullWidth
      disabled={disabled}
      disableClearable
      options={merged}
      value={sel}
      onChange={(_e, v) => onChange(v?.value ?? '')}
      PopperComponent={WideComboPopper}
      getOptionLabel={(o) => (o ? String(o.value ?? '') : '')}
      isOptionEqualToValue={(a, b) => a.value === b.value}
      renderOption={(props, o, state) => {
        const code = String(o.value ?? '')
        const lab = String(o.label ?? '')
        const text = !lab || lab === code ? code : `${code} — ${lab}`
        const { key, ...rest } = props as typeof props & { key?: React.Key }
        return (
          <li {...rest} key={`${code}__${lab}__${state.index}`} style={{ whiteSpace: 'nowrap' }}>
            {text}
          </li>
        )
      }}
      selectOnFocus
      handleHomeEndKeys
      sx={massInputSx}
      renderInput={(params) => <TextField {...params} placeholder="—" />}
    />
  )
}

/** DO No cell — options SCOPED to the row's own Contract No (mirrors the single
 *  New form `SalesAllocationFormDialog`: `useSaleAlcLookupDeliveryOrdersQuery({
 *  contract })` → BE filters `delivery_order_scs.sale_contract = :contract`). Each
 *  row calls the hook with its own contract; RTK Query dedupes identical `{contract}`
 *  args so rows sharing a contract share one request. A blank contract → `undefined`
 *  → the BE returns the unscoped DO list (same fallback as the New form).
 *
 *  Cascade (like Lot's GRN-on-stack auto-clear): once the scoped options for this
 *  row load, if the current DO No is not in that list it is CLEARED — so changing
 *  the row's Contract No drops a now-off-contract DO. An off-contract DO pasted in
 *  the same gesture that can't be validated before options load is still caught by
 *  the BE dry-run / pre-insert re-validate ("This DO does not belong to the
 *  Contract No !"). */
function DoCell({
  rowContract,
  value,
  disabled,
  onChange,
}: {
  rowContract: string
  value: string
  disabled: boolean
  onChange: (value: string) => void
}) {
  const contract = rowContract.trim()
  const doQuery = useSaleAlcLookupDeliveryOrdersQuery({ contract: contract || undefined })
  const options: SaleAlcMassLookupItem[] = useMemo(
    () => (doQuery.data?.items ?? []).map((o) => ({ value: o.value, label: o.label })),
    [doQuery.data],
  )

  // Auto-clear: once scoped options load (not while fetching), drop a DO No that is
  // not in this row's contract-scoped list. Mirrors Lot `stack_id_AfterUpdate` clear.
  useEffect(() => {
    if (doQuery.isFetching) return
    const v = value.trim()
    if (!v) return
    if (!options.some((o) => String(o.value).toLowerCase() === v.toLowerCase())) {
      onChange('')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doQuery.data, doQuery.isFetching, value, contract])

  return <ComboCell value={value} options={options} onChange={onChange} disabled={disabled} />
}
